import React from 'react';
import coverImg from './engagementmap_cover.png'
import studentImg from './student.png'
import teacherImg from './teacher.png'
import pulseImg from './pulse.png'
import limImg from './lim.png'
import slidoImg from './slido.png'
import studentViewImg from './student_view.png'
import instructorViewImg from './instructor_view.png'
import instructorViewImg2 from './instructor_view2.png'
import participantsImg from './participants.png'
import comprehensionImg from './comprehension.png'
import locationImg from './location.png'
import discussionImg from './discussion.png'
import './index.sass'
import ProjectContentContainer from "../../../components/ProjectContentContainer";
import {Grid} from "@mui/material";
import Quote from "../../../components/Quote";
import WordCard from "../../../components/WordCard";
import NumberInCircle from "../../../components/NumberInCircle";
import {MoveToTopOnMount} from "../../../utils/pageScroll";
import Head from "../../../components/Head";
import goHam from "../go_ham/go_ham_cover_bg.png";
import ProjectRecord from "../../../components/ProjectRecord";
import standUpReminder from "../stand_up_reminder/stand-up-cover-bg.png";
import bookCover1 from "../book_cover/book_cover1_thumb.png";

const QuoteEngagementMap = ({children}) => (
    <Quote color={'#A966FF'}>
        {children}
    </Quote>
)

const GridContainerEngagementMap = ({children, sx}) => (
    <Grid container spacing={'2rem'} sx={sx}>
        {children}
    </Grid>
)

const LimitationsCard = ({children}) => (
    <WordCard bgColor={'white'}>
        {children}
    </WordCard>
)

const FutureWorkCard = ({children}) => (
    <WordCard bgColor={'#FFFBF0'}>
        {children}
    </WordCard>
)

const NumberInCircleEngagementMap = ({children}) => (
    <NumberInCircle bgColor={'#A966FF'}>
        {children}
    </NumberInCircle>
)

function Index({cursor}) {
    return (
        <div>
            <Head
                title={'EngagementMap'}
                desc={'A location-based, real-time, collective, and anonymous feedback system with a spatial visualization of feelings in the classroom.'}
            />
            <MoveToTopOnMount />

            <div className="engagement_map__cover">
                <img src={coverImg} alt={'Engagement Map used on a laptop for instructors and on mobile for students'} />
            </div>

            <ProjectContentContainer>
                <h1 className={'engagement_map__title'}>
                    EngagementMap&nbsp;
                    <span className="engagement_map__title__regular">
                        is a location-based, real-time, collective, and anonymous feedback system with a
                        spatial visualization of feelings in the classroom.
                    </span>
                </h1>

                {/*Intro*/}
                <GridContainerEngagementMap sx={{marginTop: '2rem', marginBottom: '4rem'}}>
                    <Grid item xs={12} md={6}>
                        <p>
                            Student feedback is an integral part of improving a lecture; knowing how the students felt
                            about the lecture is fundamentally relevant in identifying what to change for the
                            next iteration of teaching.
                        </p>
                        <p>
                            Helping instructors improve their lecture delivery and consequently enhancing the
                            educational experience for students has been the subject of several research studies.
                            Most real-time feedback systems rely on measuring student engagement by analyzing audio or
                            video data from a classroom and presenting this data to the instructor retroactively.
                        </p>
                        <p>
                            We show that EngagementMap will help the instructors improve their lecture quality and
                            increase the level of engagement of the students by making feedback more dialogic.
                        </p>
                    </Grid>

                    <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                    <Grid item xs={12} md={5}>
                        <Grid container>
                            <Grid item xs={5} md={12}>
                                <h2 className={'engagement_map__intro__h2'}>My Role</h2>
                                <p>
                                    Needfinding, Ideation, Review Prior Research, Prototyping (Figma), Implementation
                                    (JS, HTML, CSS), User Testing
                                </p>
                                <p>
                                    As a team of 4, we presented our project in the form of an&nbsp;
                                    <a
                                        href={'https://drive.google.com/file/d/1S8FrT5yfQ0jFva5KdMK_Niu7E4jjOmCr/view'}
                                        className={'engagement_map__link'}
                                    >
                                       extended abstract to the CHI conference (link).
                                    </a>
                                </p>
                            </Grid>

                            <Grid item xs={1} sx={{ display: { md: 'none', xs: 'block' } }} />

                            <Grid item xs={5} md={12}>
                                <h2 className={'engagement_map__intro__h2'}>Results</h2>
                                <p>
                                    Conducted an in-the-wild study and validated that location-based feedback to the
                                    instructors can better direct their attention, and therefore improve student
                                    engagement.
                                </p>
                            </Grid>
                        </Grid>
                    </Grid>
                </GridContainerEngagementMap>

                <h2>Lecturing is difficult</h2>
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12} md={6}>
                        <p>
                            From our needfinding survey we found that problems students face in a classroom are
                            tied to their location.
                        </p>
                        <p>
                            For instance, the instructor may overlook a raised hand in the classroom because they
                            have their back to that section.
                        </p>
                    </Grid>

                    <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                    <Grid item xs={12} md={5}>
                        <Grid container spacing={'1.2rem'}>
                            <Grid item xs={6} md={12}>
                               <QuoteEngagementMap>
                                   "If the instructor is far from me, I feel less inclined to participate."
                                </QuoteEngagementMap>
                            </Grid>
                            <Grid item xs={6} md={12}>
                               <QuoteEngagementMap>
                                   "The lecturer often ignores the Zoom chat for remote students."
                                </QuoteEngagementMap>
                            </Grid>
                            <Grid item xs={12}>
                               <QuoteEngagementMap>
                                   "I have problems reading the materials presented on the blackboard/lecture slides
                                   because the text is too small."
                                </QuoteEngagementMap>
                            </Grid>
                        </Grid>
                    </Grid>
                </GridContainerEngagementMap>

                {/*Students goal*/}
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12} md={6}>
                        <h2>Students want to let the instructors know how they are feeling without being judged.</h2>
                        <p>
                            While students do want to let instructors know how they are feeling during class to benefit
                            more, they worry about being judged by their classmates or instructors and disrupting the
                            flow of the lecture by raising their hands.
                        </p>
                    </Grid>

                    <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                    <Grid item xs={12} md={5}>
                        <img src={studentImg} alt={'student'} className={'engagement_map__img'} />
                    </Grid>
                </GridContainerEngagementMap>

                {/*Instructors goal*/}
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12} md={6}>
                        <h2>Instructors know that feedback and engagement are helpful during lectures.</h2>
                        <p>
                            Instructors want feedback to help course-correct in real time.
                            They also would not like it to be distracting or demotivating.
                        </p>
                    </Grid>

                    <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                    <Grid item xs={12} md={5}>
                        <img src={teacherImg} alt={'teacher'} className={'engagement_map__img'} />
                    </Grid>
                </GridContainerEngagementMap>

                {/*Prior research*/}
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12}>
                        <h1>Prior Research</h1>
                    </Grid>

                    <Grid item xs={12} className={'engagement_map__grid_item_padding_top'}>
                        <h2 className={'engagement_map__clear_bottom'}>
                            Visualizing the Pulse of a Classroom
                        </h2>
                    </Grid>
                    <Grid item>
                        <GridContainerEngagementMap>
                            <Grid item xs={12} md={5}>
                                <img src={pulseImg} alt={'audio visualization of the classroom'} className={'engagement_map__img'} />
                            </Grid>

                            <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                            <Grid item xs={12} md={6}>
                                <p>
                                    This system collected audio data in real time but gave an output retroactively.
                                </p>
                                <p>
                                    The data collection was also done intrusively by another human observer observing
                                    students’ activities from the back of the classroom.
                                </p>
                            </Grid>
                        </GridContainerEngagementMap>
                    </Grid>

                    <Grid item xs={12}>
                        <h2 className={'engagement_map__clear_bottom'}>
                            Live Interest Meter
                        </h2>
                    </Grid>
                    <Grid item>
                        <GridContainerEngagementMap>
                            <Grid item xs={12} md={5}>
                                <img src={limImg} alt={'heatmap of student engagement'} className={'engagement_map__img'} />
                            </Grid>

                            <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                            <Grid item xs={12} md={6}>
                                <p>
                                    This system collected realtime feedback from students and outputted in realtime as
                                    well as retroactively of a heatmap.
                                </p>
                                <p>
                                    This brought several questions such as
                                </p>
                                <ul>
                                    <li>Is this a glanceable representation that instructors can immediately evaluate?</li>
                                    <li>Where should the instructor focus their attention upon seeing negative feedback?</li>
                                    <li>What if the problems related to speed or comprehension are being faced only by remote students?</li>
                                </ul>
                            </Grid>
                        </GridContainerEngagementMap>
                    </Grid>

                    <Grid item xs={12}>
                        <h2 className={'engagement_map__clear_bottom'}>
                            SLIDO
                        </h2>
                    </Grid>
                    <Grid item>
                        <GridContainerEngagementMap>
                            <Grid item xs={12} md={5}>
                                <img src={slidoImg} alt={'instructor and student view of SLIDO'} className={'engagement_map__img'} />
                            </Grid>

                            <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                            <Grid item xs={12} md={6}>
                                <p>
                                    SLIDO collects realtime feedback manually entered by the students. Its feedback
                                    is immediate and provides output for polls and questions that are explicitly made
                                    by the instructors.
                                </p>
                                <p>
                                    Some questions we thought of were
                                </p>
                                <ul>
                                    <li>What if instructors are not mindful about seeking feedback?</li>
                                    <li>Will such polls disrupt the flow of the lecture?</li>
                                </ul>
                            </Grid>
                        </GridContainerEngagementMap>
                    </Grid>

                </GridContainerEngagementMap>

                {/*EngagementMap*/}
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12}>
                        <h1>EngagementMap</h1>
                    </Grid>

                    <Grid item xs={12} className={'engagement_map__grid_item_padding_top'}>
                        <h2 className={'engagement_map__clear_bottom'}>
                            Student View
                        </h2>
                    </Grid>
                    <Grid item xs={12}>
                        <GridContainerEngagementMap>
                            <Grid item xs={12} md={6}>
                                <p>
                                    Students are assigned unique user ID for the purpose of our testing.
                                </p>
                                <p>
                                    They select a spatial section of the classroom they sit in.
                                    Initially, at the beginning of the class a neutral feedback is sent to the instructor.
                                    Then, at any point of the class, students can use the slider to give feedback on
                                    comprehension and interactivity.
                                </p>
                                <p>
                                    Some design choices made here to make the interface as simple and intuitive as possible.
                                </p>
                                <p>
                                    We used minimal colors to avoid distractions and used the most basic UI elements
                                    such as a dropdown and a slider that every student has used before so that students
                                    can focus more on the lecture.
                                </p>
                            </Grid>

                            <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                            <Grid item xs={12} md={5}>
                                <img
                                    src={studentViewImg}
                                    alt={'student interface of engagement map'}
                                    className={'engagement_map__img'}
                                />
                            </Grid>
                        </GridContainerEngagementMap>
                    </Grid>

                    <Grid item xs={12}>
                        <h2 className={'engagement_map__clear_bottom'}>
                            Instructor View
                        </h2>
                    </Grid>
                    <Grid item xs={12}>
                        <GridContainerEngagementMap>
                            <Grid item xs={12}>
                                <p>
                                    Instructors define the layout of the classroom in our system and divide the classroom into sections.
                                </p>
                                <p>
                                    In our experiment, the classroom was divided into four sections (left-back,
                                    right-back, front two rows, and remote).
                                </p>
                            </Grid>

                            <Grid item xs={12}>
                                <img
                                    src={instructorViewImg}
                                    alt={'instructor interface of engagement map and its spatial representation'}
                                    className={'engagement_map__img'}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <p>
                                    During the lecture, the state of each section changes in real-time with varying
                                    number of icons and colors. The variance depends on the ratio of positive and
                                    negative feedback where the thresholds are chosen according to the class size.
                                </p>
                                <p>
                                    Some design decisions that we made were that the feedback should be given spatially
                                    resembling the real classroom. This was done to lower the cognitive load of the
                                    instructor. We also carefully chose the colors to represent neutral feedback.
                                    From prior research, it is shown that positive feedback has a positive effect for
                                    teachers. Therefore, for a neutral feedback from the students, we decided to choose
                                    a lighter shade of green as the stronger shade of green represented good feedback.
                                </p>
                            </Grid>
                        </GridContainerEngagementMap>
                    </Grid>
                </GridContainerEngagementMap>

            </ProjectContentContainer>

            <img
                src={instructorViewImg2}
                alt={'instructor interface of engagement map and its spatial representation'}
                className={'engagement_map__img'}
            />

            <ProjectContentContainer>
                {/*Results*/}
                <GridContainerEngagementMap sx={{marginTop: '4rem', marginBottom: '2rem'}}>
                    <Grid item xs={12} md={6}>
                        <h1 className={'engagement_map__clear_top'}>Results</h1>
                        <p>
                            We conducted a in-the-wild study with 1 instructor and 20 students. The instructor gave a
                            30-minute lecture.
                        </p>
                        <p>
                            Following the study, we had post-study questionnaires for both the students and instructor
                            and an interview with the instructor for qualitative feedback.
                        </p>
                    </Grid>

                    <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                    <Grid item xs={12} md={5}>
                        <img
                            src={participantsImg}
                            alt={'location and number of participants in our study. 3 in the left back, 7 in the right back' +
                                '9 in the front, and 1 online'}
                            className={'engagement_map__img'}
                        />
                    </Grid>
                </GridContainerEngagementMap>

                {/*Usability*/}
                <GridContainerEngagementMap sx={{marginBottom: '4rem'}}>
                    <Grid item xs={12}>
                        <h2>Usability</h2>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <p className={'engagement_map__clear_top'}>Students sent feedback every</p>
                        <div className="engagement_map__usability__stats">
                            <NumberInCircleEngagementMap>36</NumberInCircleEngagementMap>
                            <div>seconds on average</div>
                        </div>
                    </Grid>

                    <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                    <Grid item xs={12} md={6}>
                        <Grid container spacing={'1.2rem'}>
                            <Grid item xs={6} md={12}>
                                <QuoteEngagementMap>
                                    "I thought that the interface was extremely user-friendly and easy to navigate!"
                                </QuoteEngagementMap>
                            </Grid>
                            <Grid item xs={6} md={12}>
                                <QuoteEngagementMap>
                                    "Having the indicators available at all times, I felt a bit pressured to toggle some change
                                    even when I did not necessarily need more explanation / interactivity."
                                </QuoteEngagementMap>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <p>Instructors looked at the dashboard every</p>
                        <div className="engagement_map__usability__stats">
                            <NumberInCircleEngagementMap>1</NumberInCircleEngagementMap>
                            <div>minute on average</div>
                        </div>
                    </Grid>

                    <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                    <Grid item xs={12} md={6}>
                        <QuoteEngagementMap>
                           "I wouldn’t say distract[ing]. Yes, the question marks did occupy my attention in
                            some ways, but in a good way."
                        </QuoteEngagementMap>
                    </Grid>
                </GridContainerEngagementMap>

                {/*Comprehension & Interactivity*/}
                <GridContainerEngagementMap sx={{marginBottom: '4rem'}}>
                    <Grid item xs={12}>
                        <h2>Comprehension & Interactivity</h2>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <p className={'engagement_map__clear_top'}>The figure indicates the number of feedback response over time.</p>
                        <p>
                            The first red line is when the instructor said "questions from the right-back of the
                            classroom? I see some question marks and low interactivity." Second one is when the
                            instructor slowed down their pace and explained again.
                        </p>

                        <Grid container spacing={'1.2rem'}>
                            <Grid item xs={6} md={12}>
                                <QuoteEngagementMap>
                                    Instructor: "There are a couple times I saw question marks pop up as I was
                                    explaining concepts, and it caught me by surprise a little bit, so I interrupted my
                                    explanation just to ask ’hey is there any questions?'"
                                </QuoteEngagementMap>
                            </Grid>
                            <Grid item xs={6} md={12}>
                                <QuoteEngagementMap>
                                    Student: "I’m not sure if it was directly due to my indicator explicitly but he
                                    explained the topic I needed clarification on in further detail."
                                </QuoteEngagementMap>
                            </Grid>
                            <Grid item xs={6} md={12}>
                                <QuoteEngagementMap>
                                    Student: "I noticed that he slowed down the pace of the lecture"
                                </QuoteEngagementMap>
                            </Grid>
                            <Grid item xs={6} md={12}>
                                <QuoteEngagementMap>
                                    Student: "I could especially tell that the instructor often slowed down and made
                                    more intention to maintain eye contact with the audience or become more animated
                                    in his speech"
                                </QuoteEngagementMap>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                    <Grid item xs={12} md={6}>
                        <img
                            src={comprehensionImg}
                            alt={'time series data of comprehension and interactivity in students'}
                            className={'engagement_map__img'}
                        />
                    </Grid>

                </GridContainerEngagementMap>

                {/*Location*/}
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12}>
                        <h2>Location</h2>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <p className={'engagement_map__clear_top'}>Figure of the level of students’ comprehension/interactivity over time. </p>
                        <p>
                            The level is calculated by number of positive feedback - number of negative feedback.
                            Only the front (n=9) and right-back (n=7) sections are shown for enough data points.
                        </p>

                        <Grid container spacing={'1.2rem'}>
                            <Grid item xs={6} md={12}>
                                <QuoteEngagementMap>
                                    Instructor: "It did feel nice to be able to target a general area of the
                                    classroom when I was asking if people had any questions."
                                </QuoteEngagementMap>
                            </Grid>
                            <Grid item xs={6} md={12}>
                                <QuoteEngagementMap>
                                    Instructor: "It (location-based feedback) helped with my intervention process
                                    because it makes students feel like they were listened to more when I address that section"
                                </QuoteEngagementMap>
                            </Grid>
                            <Grid item xs={6} md={12}>
                                <QuoteEngagementMap>
                                    Instructor: "Maybe with practice and continued use of this system, I can get
                                    better at that (incorporate location-based feedback in his live teaching)"
                                </QuoteEngagementMap>
                            </Grid>
                            <Grid item xs={6} md={12}>
                                <QuoteEngagementMap>
                                    Student: "He turned his attention to a specific group and prompted questions"
                                </QuoteEngagementMap>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                    <Grid item xs={12} md={6}>
                        <img
                            src={locationImg}
                            alt={'time series data of comprehension and interactivity in students with respect to location'}
                            className={'engagement_map__img'}
                        />
                    </Grid>

                </GridContainerEngagementMap>

                {/*Discussion*/}
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12}>
                        <h1>Discussion</h1>
                    </Grid>

                    <Grid item xs={12} className={'engagement_map__grid_item_padding_top'}>
                        <p>
                            A socio-constructivist perspective of feedback posits that feedback should be dialogic
                            and help to develop students’ ability to monitor, evaluate and regulate their learning.
                            We envision this system as a step in this direction.
                        </p>
                    </Grid>

                    <Grid item xs={12}>
                        <QuoteEngagementMap>
                            “As a student I loved using this, because I felt incentivized to check in with myself
                            and my own comprehension, and therefore felt like I was absorbing the material better.
                            I also felt like there is already a layer of interactivity introduced simply by inviting
                            students to provide their feedback to the professor, so that they feel that they
                            are participating.”
                        </QuoteEngagementMap>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <p className={'engagement_map__clear_top'}>
                            Our study highlighted that some students felt uncomfortable "rating" the instructor, and
                            there was a general perception of a “blame" associated with entering negative feedback.
                            EngagementMap is not a rating system. We believe that it would best be used as a
                            system to augment communication in the classroom.
                        </p>
                        <p>
                            Aggregating student feedback makes it glanceable and less distracting for the instructor.
                        </p>
                        <p>
                            Aggregating based on location affords an increased granularity of feedback while still
                            keeping it anonymous. It also helps the instructor determine where to focus their
                            attention if and when they decide to act on it.
                        </p>
                    </Grid>

                    <Grid item md={1} sx={{ display: { md: 'block', xs: 'none' } }} />

                    <Grid item xs={12} md={5}>
                        <img
                            src={discussionImg}
                            alt={'two people discussing'}
                            className={'engagement_map__img'}
                        />
                    </Grid>

                </GridContainerEngagementMap>

                {/*Limitations*/}
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12}>
                        <h1>Limitations</h1>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container spacing={'1.2rem'}>
                            <Grid item xs={6} md={3} sx={{marginBottom: {'xs': '2rem'}}}>
                                <LimitationsCard>
                                    Focused on 2 parameters (comprehension and interactivity)
                                </LimitationsCard>
                            </Grid>
                            <Grid item xs={6} md={3} sx={{marginBottom: {'xs': '2rem'}}}>
                                <LimitationsCard>
                                    Meaning of “interactivity” was ambiguous
                                </LimitationsCard>
                            </Grid>
                            <Grid item xs={6} md={3} sx={{marginBottom: {'xs': '2rem'}}}>
                                <LimitationsCard>
                                    Slider had discrete values
                                </LimitationsCard>
                            </Grid>
                            <Grid item xs={6} md={3} sx={{marginBottom: {'xs': '2rem'}}}>
                                <LimitationsCard>
                                    Had an n = 1 study
                                </LimitationsCard>
                            </Grid>
                        </Grid>
                    </Grid>
                </GridContainerEngagementMap>

                {/*Future Work*/}
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12}>
                        <h1>Future Work</h1>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container spacing={'1.2rem'}>
                            <Grid item xs={6} md={3} sx={{marginBottom: {'xs': '2rem'}}}>
                                <FutureWorkCard>
                                    Extend to include other parameters
                                </FutureWorkCard>
                            </Grid>
                            <Grid item xs={6} md={3} sx={{marginBottom: {'xs': '2rem'}}}>
                                <FutureWorkCard>
                                    Further study effect on hybrid lecture
                                </FutureWorkCard>
                            </Grid>
                            <Grid item xs={6} md={3} sx={{marginBottom: {'xs': '2rem'}}}>
                                <FutureWorkCard>
                                    Add a reminder feature to prevent drop in responses
                                </FutureWorkCard>
                            </Grid>
                            <Grid item xs={6} md={3} sx={{marginBottom: {'xs': '2rem'}}}>
                                <FutureWorkCard>
                                    Embed the system in the classroom
                                </FutureWorkCard>
                            </Grid>
                        </Grid>
                    </Grid>
                </GridContainerEngagementMap>

                {/*Other Work*/}
                <GridContainerEngagementMap sx={{marginBottom: '6rem'}}>
                    <Grid item xs={12}>
                        <h1>Other Work</h1>

                        <Grid container spacing={'2rem'}>
                            <Grid item xs={12}>
                                <ProjectRecord
                                    to={'/work/GO-HAM'}
                                    title={'GO HAM!'}
                                    subtitle={'Re-establishing the Columbia community during COVID-19 through exercise'}
                                    desc={'Exercising app with emphasis on spontaneous human interaction through gamification. ' +
                                    'Helps motivate and keep students in a routine to exercise in a limited space with friends.'}
                                    img={goHam}
                                    imgAlt={'smart phone app design of go ham'}
                                    bgColor={'linear-gradient(127.67deg, rgba(17, 141, 255, 0.2) 60.94%, rgba(217, 242, 249, 0.2) 76.79%, rgba(255, 255, 255, 0.0252632) 92.97%), #D5FFCB'}
                                    cursor={cursor}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <ProjectRecord
                                    to={'/work/Alexa-Stand-Up-Reminder'}
                                    color={'dark'}
                                    title={'Alexa Stand Up Reminder'}
                                    subtitle={'Sitting for a long time has health concerns and we wanted to help you in a natural way'}
                                    desc={'Reminds you to stand up every 45 minutes and take a break for 5 minutes. During the break, it suggests  fun and relaxing activities!'}
                                    img={standUpReminder}
                                    imgAlt={'logo with alexa'}
                                    bgColor={'linear-gradient(114.74deg, rgba(0, 114, 255, 0.5) 56.88%, rgba(167, 125, 255, 0.265652) 97.5%, rgba(255, 255, 255, 0.0631579) 113.08%), #FF583B'}
                                    cursor={cursor}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <ProjectRecord
                                    to={'/work/book-covers'}
                                    title={'Published Book Covers'}
                                    subtitle={'I designed 2 book covers that were published in Japan'}
                                    img={bookCover1}
                                    imgAlt={'logo of the book cover i designed about inclusive education'}
                                    bgColor={'linear-gradient(127.67deg, rgba(17, 141, 255, 0.3) 60.94%, rgba(217, 242, 249, 0.3) 76.79%, rgba(255, 255, 255, 0.0378947) 92.97%), #F3FFF0'}
                                    cursor={cursor}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </GridContainerEngagementMap>

            </ProjectContentContainer>
        </div>
    );
}

export default Index;